import API from 'shared/constants/api';
import snakeCaseKeys from 'snakecase-keys';
import queryString from 'query-string';
import pick from 'lodash/pick';
import isEmpty from 'lodash/isEmpty';
import {
  setFormVocabulary,
  selectedActions,
  dealersFormFillThunks,
  resultsThunks,
  dealersSetThunks,
  resetAll,
} from 'shared/actions/searchForm';
import CITIES from 'shared/constants/json/cities.json';
import COUNTRIES from 'shared/constants/json/countries.json';
import REGIONS from 'shared/constants/json/regions.json';
import { getSeoParamsTitles } from 'shared/helpers/arrays';
import { replaceSpace } from 'shared/modules';
import { setLocation, setSeoSearchUrl, seoRequest } from 'shared/actions/globals';
import { getResultFromStoreByKey } from 'shared/helpers/common';
import types from 'shared/constants/json/transportTypes.json';
import DESCRIPTION from 'shared/constants/seoDescriptions';
import TITLE from 'shared/constants/seoTitles';
import META_HEADER from 'shared/constants/seoHeaders';
import LINKS from 'desktop/constants/navigation';
import { getAdsContext, getPriceValuation } from 'shared/helpers/adsAndMetrics';
import { removeLocaleFromHref } from 'shared/helpers/locale';
import subjectiveText from 'shared/modules/subjectiveText';
import { getRootTransportType } from 'shared/helpers/transportTypes';

export default async function(
  { fetch, store, baseUrl, path, query, translate },
  device = 'desktop',
) {
  const missingVocabularyFields = [];
  const url = removeLocaleFromHref(`${baseUrl}${path}`.replace('//', '/'));
  const state = store.getState();
  const {
    globals: { locale, location: storeLocation },
    searchForm: { vocabulary },
  } = state;
  const currentPage = query.page ? parseInt(query.page, 10) : 1;

  if (storeLocation && storeLocation.is_country) {
    store.dispatch(setLocation(null));
  }

  let seoQuery = await store.dispatch(seoRequest(url));

  if (isEmpty(seoQuery)) {
    return null;
  }
  if (seoQuery.location && seoQuery.location.is_country) {
    return null;
  }

  const { seo: seoTags, kind, meta, ...seoResponse } = seoQuery;
  let seoAPITitle = '';
  let seoAPIDescription = '';
  let seoAPIH1 = '';

  store.dispatch(selectedActions.set.powerUnit(null));
  // store.dispatch(selectedActions.set.currency(null));
  store.dispatch(selectedActions.set.dealer(null));
  store.dispatch(selectedActions.set.capacityUnit(null));

  const transportType = seoQuery.transport || types.find(item => item.slug === 'avto');
  if (!vocabulary.categories) {
    missingVocabularyFields.push('categories');
  }
  const fetchedVocabulary = await Promise.all(
    missingVocabularyFields.map(field =>
      API.VOCABULARY[field]
        ? fetch(API.VOCABULARY[field](transportType.id))
            .then(r => r.json())
            .then(response => ({ [field]: response }))
        : Promise.resolve({}),
    ),
  );
  const mergedVocabulary = fetchedVocabulary.reduce(
    (results, pair) => ({ ...results, ...pair }),
    {},
  );
  store.dispatch(
    setFormVocabulary({
      ...mergedVocabulary,
    }),
  );
  const {
    searchForm: {
      vocabulary: { categories },
    },
  } = store.getState();

  let body;
  for (let a = 0; a < categories.length; a += 1) {
    const buf = categories[a] && categories[a].title && categories[a].id;
    if (buf && query.category && String(buf) === query.category) {
      body = categories[a];
      break;
    }
  }
  const transportID = transportType.id;

  const prevSelectedTransport = state.searchForm.selected && state.searchForm.selected.transport;
  if (prevSelectedTransport && prevSelectedTransport.id !== transportID) {
    store.dispatch(resetAll());
  }
  store.dispatch(selectedActions.set.transport(transportType));

  const storeParams = Object.entries(store.getState().searchForm.selected)
    .map(([key, value]) => (value && value.id ? [key, value.id] : [key, value]))
    .filter(([, value]) => !!value && (typeof value === 'string' || typeof value === 'number'))
    .reduce((result, item) => ({ ...result, [item[0]]: item[1] }), {});

  if (transportType.slug === 'avto') {
    delete storeParams.tags;
  }

  const q = { ...storeParams, ...query };

  let location = store.getState() && store.getState().globals.location;
  if (q.model && !seoQuery.model) {
    seoQuery.model = await fetch(API.SEO.SEARCH_MODEL(q.model)).then(r => (r.ok ? r.json() : {}));
  }
  if (seoQuery.make) {
    q.make = seoQuery.make.id;
  }
  /* if (seoQuery.model) {
    q.model = seoQuery.model.id;
  } */
  if (seoQuery.location) {
    location = seoQuery.location;
    store.dispatch(setLocation(seoQuery.location));
  }

  if (typeof seoTags === 'object') {
    const LangPrefix = locale === 'ru' ? '' : `${locale}_`;
    seoAPIDescription = seoTags[`${LangPrefix}description`] || '';
    seoAPITitle = seoTags[`${LangPrefix}title`] || '';
    const seoTagsData = seoTags[`${LangPrefix}data`] || null;
    seoAPIH1 = seoTagsData && (seoTagsData.H1 || seoTagsData.h1);
  }

  const hasNew = query.hasNew || store.getState().searchForm.selected.hasNew || null;

  q.location = (location && location.id) || undefined;
  q.transport = transportID;
  if (!isNaN(hasNew) && hasNew !== null) {
    q.hasNew = hasNew;
  }

  const formQueryState = pick(q, Object.keys(state.searchForm.selected));
  store.dispatch(dealersSetThunks.count(formQueryState));

  const makes = await fetch(API.VOCABULARY.makes(transportID)).then(r => r.json());

  store.dispatch(
    setFormVocabulary({
      makes,
    }),
  );

  const queryParams = queryString.stringify(snakeCaseKeys(q));

  await store.dispatch(resultsThunks.searchDealersResults(queryParams));

  Object.keys(q).forEach(key => {
    const item = q[key];
    if (dealersFormFillThunks[key]) {
      store.dispatch(dealersFormFillThunks[key](item));
    }
  });

  const transportLinks =
    device !== 'mobile' &&
    LINKS.AVTOSALONS_LINKS.map(({ route, ...rest }) => ({
      ...rest,
      route: location ? `${route}/${location.slug}/`.replace(/\/\//g, '/') : route,
    }));

  let { year } = seoQuery;
  if (!year && query.yearFrom === query.yearTo) year = query.yearFrom;

  const seoLocation = [...CITIES, ...REGIONS, ...COUNTRIES].find(
    item => item.id === (location && location.id),
  );

  const seoParams = getSeoParamsTitles({ ...seoQuery, location, country: seoLocation }, translate);

  let description = replaceSpace(DESCRIPTION.DEALERS(seoParams, transportType.slug, translate));

  let title = replaceSpace(TITLE.DEALERS(seoParams, transportType.slug, translate));

  let metaHeader = replaceSpace(META_HEADER.DEALERS(seoParams, transportType.slug, translate));

  const result = getResultFromStoreByKey(store, `dealers-${queryParams}`);
  const isLastPage = !(result && result.next);

  const isAuto = transportType.slug === 'avto';
  const lang = state.globals.locale;
  const country = seoLocation && seoLocation.is_country ? seoLocation.from[locale] : undefined;
  const { make = {}, model = {} } = seoQuery;
  const transportTypeTitle = (transportType && transportType.title) || '';
  const transportCategoryTitle =
  (seoQuery.transport_category && seoQuery.transport_category.title) || '';
  const defaultCategoryDescriptions =
  model.title && !seoQuery.category ? translate('авто') : translate('легковой автомобиль');
  const defaultCategoryTitleHeader = model.title;
  let drive;
  const seo = {
    ...seoQuery,
    location: seoLocation,
  };

  const metaParams = {
    transport: transportType,
    type: transportType,
    make: seoQuery.make,
    model: seoQuery.model,
    modification: seoQuery.modification,
    category: seoQuery.category,
    gearbox: seoQuery.gearbox,
    engine: seoQuery.engine,
    location: location && `в ${location.title}`,
    yearTo: q.yearTo,
    yearFrom: q.yearFrom,
    isNew: q.isNew,
    country,
    year: seoQuery.year,
    body: q.body
  };

  const commonArguments = subjectiveText({
    make: (make && make.title) || '',
    makeTranslit: make[`${lang}_title`] || make.title || '',
    model: model.title || '',
    modelTranslit: model[`${lang}_title`] || model.title || '',
    transportTitle: (isAuto && transportCategoryTitle) || transportTypeTitle,
    transportNextTitle: transportTypeTitle,
    transportDescriptions: (isAuto && transportCategoryTitle) || transportTypeTitle,
    transportNextDescriptions: transportTypeTitle,
    transportHeader: transportCategoryTitle || transportTypeTitle,
    transportNextHeader: transportCategoryTitle || transportTypeTitle,
    categoryHeader: !model && !model.title && body && body.title ? body.title : defaultCategoryTitleHeader,
    categoryTitle: body && body.title || defaultCategoryTitleHeader,
    categoryDescriptions: body && body.title || defaultCategoryDescriptions,
    reductionTranslit: transportType.slug === 'avtobus' ? 'бус' : '',
    cityTitle: (location && location.title) || 'Украина',
    cityDescriptions: (location && location.title) || 'Украина',
    cityHeader: (location && location.title) || 'Украина',
    transportCategoryTitle,
    generation:
      (query.generation && seoQuery.generation && seoQuery.generation.title) ||
      (query.generation && ' ') ||
      '',
    driveUnit: drive || '',
    engine: (seo.engine && seo.engine.title) || '',
    transmission: (metaParams.gearbox && metaParams.gearbox.title) || '',
    body: body && body.title || '',
    subsection: (seoQuery && seoQuery.part_type && seoQuery.part_type.title) || '',
    isNew: q.isNew,
    transportType: (transportType && transportType.slug) || '',
    allData: {
      url,
      baseUrl,
      locale,
      translate,
    },
    yearTitle: year ? `${year} ${translate('года')} ` : '',
    yearDescriptions: year ? `${year} ${translate('г.')} ` : '',
    motoHeader: transportCategoryTitle,
    motoTitle: transportCategoryTitle,
  });

  const rootTransportType = getRootTransportType(transportType);
  const type = rootTransportType.slug;
  const allMetaParams = { ...commonArguments,...metaParams };


  if (rootTransportType.slug === 'moto') {
    title = seoAPITitle || TITLE.DEALERS({
      allMetaParams,
      make: make && make.title ? make.title : '',
      model: model && model.title ? model.title : '',
      location: location && location.title ? location.title : '',
    }, 'mototsikl',translate);
    description =
      seoAPIDescription || DESCRIPTION.DEALERS({
        allMetaParams,
        make: make && make.title ? make.title : '',
        model: model && model.title ? model.title : '',
        location: location && location.title ? location.title : '',
      }, 'mototsikl',translate);
    metaHeader = seoAPIH1 || META_HEADER.DEALERS({
      allMetaParams,
      make: make && make.title ? make.title : '',
      model: model && model.title ? model.title : '',
      location: location && location.title ? location.title : '',
    }, 'mototsikl',translate);
  } else {
    title = seoAPITitle || TITLE.DEALERS({
      allMetaParams,
      make: make && make.title ? make.title : '',
      model: model && model.title ? model.title : '',
      location: location && location.title ? location.title : '',
    }, type,translate);
    description =
      seoAPIDescription || DESCRIPTION.DEALERS({
        allMetaParams,
        make: make && make.title ? make.title : '',
        model: model && model.title ? model.title : '',
        location: location && location.title ? location.title : '',
      }, type,translate);
    metaHeader = seoAPIH1 || META_HEADER.DEALERS({
      allMetaParams,
      make: make && make.title ? make.title : '',
      model: model && model.title ? model.title : '',
      location: location && location.title ? location.title : '',
    }, type,translate);
  }


  const targetingArguments = getAdsContext({
    make: seo.make,
    model: seo.model,
    isMain: seo.isMain,
    isNew: seo.isNew,
    rubric: seo.rubric,
    isCustomCleared: seo.isCustomCleared,
    location: seo.location,
    isCountry: seo.isCountry,
    transport: transportType,
    category: body,
    engine: seo.engine,
    dealer: seo.rubric,
    price: getPriceValuation({ from: q.priceFrom, to: q.priceTo }, q.currency),
    page: baseUrl.replace(/\//g, ''),
    id: seo.slug,
  });
  const topDealerRequestObj = {
    transport: transportType ? transportType.id : 1,
    ...(q.make ? { make: q.make } : {}),
  };
  store.dispatch(setSeoSearchUrl({ ...seoQuery, url }));

  return {
    url,
    currentPage,
    targetingArguments,
    description,
    title,
    queryParams,
    metaHeader,
    topDealerRequestObj,
    isLastPage,
    transportLinks,
    location,
    transportType,
    seoQuery,
    q,
  };
}



// WEBPACK FOOTER //
// ./src/shared/routes/dealers/index.js